import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of, switchMap, tap } from 'rxjs';
import { MembershipActions } from './membership.actions';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs/operators';
import { getResponseKey } from '../utils/get-response-key';
import { ExecuteService } from '../services/execute.service';
import { ClubApp } from '@aaa/emember/types';
import { LocationRefService } from '../../modules/share/services/location-ref.service';
import { filterByClubIds } from '../utils/filter-by-club-ids';
import { getSaveCacheSettings } from '../utils/get-cache-settings';
import { Pages } from '../../constants/pages';
import {
  MembershipCampanaGetAccountEvent,
  MembershipCampanaMethod,
} from '@aaa/interface-joinRenew-membership-membershipCampana';
import { CampanaType } from '../campana.type';
import { checkMembershipErrorsCampanaSystem } from '../check-membership-errors-campana-system';

@Injectable({ providedIn: 'root' })
export class MembershipCampanaSystemEffects {
  store = inject(Store);
  executeService = inject(ExecuteService);
  location = inject(LocationRefService);
  actions$ = inject(Actions).pipe(filterByClubIds([ClubApp.Northway]));

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MembershipActions.logout),
        tap(() => (this.location.nativeLocation.href = this.location.nativeLocation.origin + Pages.accountLogout)),
      ),
    { dispatch: false },
  );

  loadMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.load),
      switchMap(({ membershipNumber, enableCache }) =>
        this.loadMembership(membershipNumber, enableCache).pipe(
          map((accountDetails) => MembershipActions.loadSucceeded({ accountDetails })),
          catchError((error) => of(MembershipActions.loadFailed({ error }))),
        ),
      ),
    ),
  );

  /**
   * One call: GET_ACCOUNT is the summary and the session read merged backend side, because
   * neither half describes an account on its own. `details` is null when AXIS would not give
   * up a session - the page keeps identity, address and contacts, and loses only the level
   * and the balance.
   */
  loadMembership(membershipNumber: string, enableCache = true) {
    const payload: MembershipCampanaGetAccountEvent = {
      cacheSettings: enableCache ? getSaveCacheSettings({ context: membershipNumber }) : undefined,
      memberNumber: membershipNumber,
      method: MembershipCampanaMethod.GET_ACCOUNT,
      responseKey: getResponseKey(),
    };

    return this.executeService.membershipQuery<CampanaType.GetAccountResponseObject>(payload).pipe(
      map((accountResponseObject) => {
        // MEMBERSHIP_RECORD_UNAVAILABLE arrives as MembershipCancelled, which routes to the
        // cancelled experience rather than failing the page load outright
        checkMembershipErrorsCampanaSystem(accountResponseObject.error, accountResponseObject);

        const { summary, details } = accountResponseObject.response;

        return new CampanaType.AccountInfo(membershipNumber, summary, details);
      }),
    );
  }
}
